import type { Skill } from "#types/types.ts";
import { foldForMatching } from "./textMatch.ts";

const rankSkillAgainstQuery = (skill: Skill, foldedQuery: string): number | null => {
    const foldedTitle = foldForMatching(skill.title);
    if (foldedTitle.startsWith(foldedQuery)) {
        return 0;
    }
    const foldedSlug = foldForMatching(skill.slug);
    if (foldedSlug.startsWith(foldedQuery)) {
        return 1;
    }
    if (foldedTitle.includes(foldedQuery) || foldedSlug.includes(foldedQuery)) {
        return 2;
    }

    return null;
};

// Backend order; a query keeps title-prefix matches first, then slug-prefix, then substring.
export const searchSkills = (skills: Skill[], query: string): Skill[] => {
    const foldedQuery = foldForMatching(query);
    if (foldedQuery === "") {
        return skills;
    }

    return skills
        .map((skill, originalIndex) => ({ skill, originalIndex, rank: rankSkillAgainstQuery(skill, foldedQuery) }))
        .filter((entry): entry is { skill: Skill; originalIndex: number; rank: number } => entry.rank !== null)
        .sort((a, b) => a.rank - b.rank || a.originalIndex - b.originalIndex)
        .map(entry => entry.skill);
};

type SkillLanguage = keyof NonNullable<Skill["displayedTitle"]>;

export const toSkillLanguage = (language: string | undefined): SkillLanguage => language?.startsWith("fr") ? "fr" : "en";

// The UI shows the human-facing displayed text; title and description are the model-facing English and the fallback.
export const localizeSkill = (skill: Skill, language: SkillLanguage): Skill => ({
    ...skill,
    title: skill.displayedTitle?.[language] ?? skill.title,
    description: skill.displayedDescription?.[language] ?? skill.description
});
